/**
 * /recensione (no token) — for a visitor who reaches the address without a
 * personal link: an invitation to review on Google, and a line on where
 * verified reviews come from. Client-rendered from App.tsx like the token
 * pages, so it needs no route, OG card, sitemap entry or prerender
 * exception; noindex (useNoindexHead).
 *
 * The Google button appears only when the sede has a link (public list
 * endpoint, §4); until the answer arrives its place is reserved.
 */
import { useEffect, useState } from 'react'
import type { SedeConfig } from '../sedi/types'
import { fetchPublicReviews } from './api'
import { PAGE } from './strings'
import { ReviewChrome, useNoindexHead } from './ReviewChrome'

export default function ReviewInvitePage({ sede }: { sede: SedeConfig }) {
  useNoindexHead(`${PAGE.kicker} · ${sede.brand.name}`)
  // undefined while loading, '' when there is no link (or the call failed).
  const [googleUrl, setGoogleUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    fetchPublicReviews(sede.sedeId).then((r) => {
      if (!cancelled) setGoogleUrl(r.ok ? r.data.google_review_url : '')
    })
    return () => {
      cancelled = true
    }
  }, [sede.sedeId])

  return (
    <ReviewChrome sede={sede}>
      <div className="rf-card rv-sections">
        <div>
          <p className="rv-kicker">{PAGE.kicker}</p>
          <h1 className="rv-title">{PAGE.inviteTitle}</h1>
          <p className="rv-lead">{PAGE.inviteBody(sede.brand.name)}</p>
        </div>
        {googleUrl === undefined && <div className="rv-skeleton" aria-hidden="true" />}
        {googleUrl && (
          <div>
            <p className="rv-p" style={{ marginBottom: 12 }}>
              {PAGE.inviteGoogle}
            </p>
            <a className="rf-btn rv-block" href={googleUrl} target="_blank" rel="noopener noreferrer">
              {PAGE.inviteGoogleButton}
              <span className="sr-only"> {PAGE.newTab}</span>
            </a>
          </div>
        )}
        <p className="rv-p">{PAGE.inviteVerified}</p>
      </div>
    </ReviewChrome>
  )
}
